'use client'

import { FC, useEffect, useState } from 'react'

import ContactForm from '@/app/forgot-password/components/ContactForm'
import {
  MethodEnum,
  StepEnum,
} from '@/app/forgot-password/components/ForgotPassword/types'
import ModalInformation from '@/app/forgot-password/components/ModalInformation'
import { VariantType } from '@/app/forgot-password/components/ModalInformation/types'
import ResetPasswordForm from '@/app/forgot-password/components/ResetPasswordForm'
import { IApiError } from '@/common/interfaces'
import { ModalBase } from '@/components/commons'
import { GetContactByDocumentMultiTenantService } from '@/services/passwordReset/getContactByDocumentMultiTenantService/getContactByDocumentMultiTenantService'
import {
  IGetContactByDocumentMultiTenant,
  IGetContactByDocumentMultiTenantErrorResponse,
} from '@/services/passwordReset/getContactByDocumentMultiTenantService/interfaces/response'
import {
  IResetPasswordMultiTenantRequest,
  ResetPasswordMultiTenantService,
} from '@/services/passwordReset/resetPasswordMultiTenantService/resetPasswordMultiTenantService'
import {
  ISendCodeResetMultiTenantRequest,
  SendCodeResetMultiTenantService,
} from '@/services/passwordReset/sendCodeResetMultiTenantService/sendCodeResetMultiTenantService'

import { IForgotPasswordModalProps } from './types'

const ForgotPasswordModal: FC<IForgotPasswordModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  document,
  tenantId,
}) => {
  const [step, setStep] = useState<StepEnum>(StepEnum.CONTACT)
  const [inputLoading, setInputLoading] = useState(false)
  const [sendButtonLoading, setSendButtonLoading] = useState(false)
  const [isOpenInformationModal, setIsOpenInformationModal] = useState(false)
  const [modalVariant, setModalVariant] = useState<VariantType>('customMessage')
  const [customMessageError, setCustomMessageError] = useState<
    Array<IApiError> | string
  >([] as Array<IApiError>)
  const [contactResponse, setContactResponse] =
    useState<IGetContactByDocumentMultiTenant>(
      {} as IGetContactByDocumentMultiTenant,
    )
  const [method, setMethod] = useState<MethodEnum>(MethodEnum.SMS)
  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenantId)

  // Buscar contatos ao abrir o modal
  useEffect(() => {
    if (isOpenModal && document && tenantId) {
      handleGetContact()
    } else {
      // Resetar estados ao fechar
      setStep(StepEnum.CONTACT)
      setContactResponse({} as IGetContactByDocumentMultiTenant)
      setSelectedTenantId(tenantId)
      setIsOpenInformationModal(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal])

  async function handleGetContact() {
    setInputLoading(true)

    await GetContactByDocumentMultiTenantService({ document, tenantId })
      .then((response) => {
        if (response?.data) {
          setContactResponse(response.data)
          setStep(StepEnum.CONTACT)
        }
      })
      .catch((error: IGetContactByDocumentMultiTenantErrorResponse) => {
        setCustomMessageError(
          error?.response?.data ?? 'Erro ao tentar procurar o usuário.',
        )
        setModalVariant('customMessage')
        setIsOpenInformationModal(true)
      })
      .finally(() => setInputLoading(false))
  }

  async function handleSendCodeReset(params: ISendCodeResetMultiTenantRequest) {
    setSendButtonLoading(true)

    await SendCodeResetMultiTenantService({
      document: params.document,
      method: params.method,
      tenantId: params.tenantId,
    })
      .then((response) => {
        if (response.status === 200) {
          setSelectedTenantId(params.tenantId)
          setStep(StepEnum.CONFIRM_RESET)
        }
      })
      .catch((error: IGetContactByDocumentMultiTenantErrorResponse) => {
        setCustomMessageError(error?.response?.data ?? 'Erro ao enviar código.')
        setModalVariant('customMessage')
        setIsOpenInformationModal(true)
      })
      .finally(() => setSendButtonLoading(false))
  }

  async function handleResetPassword(params: IResetPasswordMultiTenantRequest) {
    setSendButtonLoading(true)

    await ResetPasswordMultiTenantService(params)
      .then((response) => {
        if (response.status === 200) {
          setModalVariant('resetPasswordSuccess')
          setIsOpenInformationModal(true)
        }
      })
      .catch((error: IGetContactByDocumentMultiTenantErrorResponse) => {
        setCustomMessageError(
          error?.response?.data ?? 'Erro ao tentar alterar a senha.',
        )
        setModalVariant('customMessage')
        setIsOpenInformationModal(true)
      })
      .finally(() => setSendButtonLoading(false))
  }

  function handleCloseModal() {
    setIsOpenInformationModal(false)
    setIsOpenModal(false)
  }

  return (
    <ModalBase
      className="w-full max-w-[437px]"
      isOpenModal={isOpenModal}
      setIsOpenModal={(value) => setIsOpenModal(value as boolean)}
      positionCenter
    >
      <ModalBase.Header>
        <h6 className="prose-headline-h6 text-primary-900">
          {step === StepEnum.CONTACT
            ? 'Recuperação de senha'
            : step === StepEnum.CONFIRM_RESET ||
                step === StepEnum.CONFIRM_RESET_WITH_CODE
              ? 'Recuperação de senha'
              : 'Recuperação de senha'}
        </h6>
      </ModalBase.Header>

      <ModalBase.Body>
        {inputLoading ? (
          <div className="flex justify-center items-center py-8">
            <p className="prose-body text-primary-900">
              Carregando contatos...
            </p>
          </div>
        ) : step === StepEnum.CONTACT ? (
          <ContactForm
            contacts={contactResponse}
            handleSendCodeReset={handleSendCodeReset}
            sendButtonLoading={sendButtonLoading}
            setStep={setStep}
            setMethod={setMethod}
            onCancel={() => setIsOpenModal(false)}
          />
        ) : step === StepEnum.CONFIRM_RESET ||
          step === StepEnum.CONFIRM_RESET_WITH_CODE ? (
          <ResetPasswordForm
            key={`reset-password-${isOpenModal}-${step}`}
            step={step}
            setStep={setStep}
            handleResetPassword={handleResetPassword}
            sendButtonLoading={sendButtonLoading}
            handleSendCodeReset={handleSendCodeReset}
            contacts={contactResponse}
            method={method}
            selectedTenantId={selectedTenantId}
          />
        ) : null}
      </ModalBase.Body>

      {isOpenInformationModal ? (
        <ModalInformation
          variant={modalVariant}
          isOpenModal={isOpenInformationModal}
          setIsOpenModal={setIsOpenInformationModal}
          customMessage={customMessageError}
          handleGoToLogin={handleCloseModal}
        />
      ) : null}
    </ModalBase>
  )
}

export default ForgotPasswordModal
